import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, InputLabel, MenuItem, Select, Typography, Chip,
  IconButton, TextField, Switch, FormControlLabel, Collapse, Tooltip, Badge,
  InputAdornment, Snackbar,
} from '@mui/material';
import {
  Wifi as WifiIcon, Bluetooth as BluetoothIcon, CellTower as CellIcon,
  Pause as PauseIcon, PlayArrow as PlayIcon, Delete as ClearIcon,
  Search as SearchIcon, FiberManualRecord as DotIcon,
  SignalCellularAlt as RssiIcon, MyLocation as ZoomIcon,
  PlaylistAddCheck as WatchlistIcon, QrCode2 as QrCodeIcon,
  FactCheck as FactCheckIcon,
} from '@mui/icons-material';
import { apiService } from '@signals/app-shell/services/api';
import PromoteToPacketDialog, { PromoteTarget } from '@signals/app-shell/components/intel/PromoteToPacketDialog';
import { useFeatureFlagsStore } from '@signals/app-shell/stores/featureFlagsStore';
import { sseSignalService, SignalEvent } from '../../services/sseSignalService';
import WiFiIEPanel from '../shared/WiFiIEPanel';
import MqttSetupQrDialog from '../shared/MqttSetupQrDialog';
import {
  bundleWatchlistDraft,
  selectedWatchlistIdentities,
  WatchlistBundle,
} from './watchlistIdentities';

const MAX_SIGNALS = 500;

const DEVICE_TYPE_COLORS: Record<string, string> = {
  'Phone': '#26a69a', 'Tablet': '#26a69a', 'Laptop': '#5c6bc0',
  'Wearable': '#ab47bc', 'Headphones': '#ab47bc', 'Speaker': '#7e57c2',
  'TV / Display': '#5c6bc0', 'Beacon': '#ff9800',
  'Tracker': '#e91e63', 'Vehicle Hotspot': '#42a5f5', 'Phone Hotspot': '#26a69a',
  'IoT Sensor': '#78909c', 'Drone': '#f44336', 'WiFi AP': '#66bb6a',
  'Cell Tower': '#7e57c2', 'Unknown': '#9e9e9e',
};

const SIGNAL_ICONS: Record<string, React.ReactNode> = {
  wifi: <WifiIcon fontSize="small" sx={{ color: '#42a5f5' }} />,
  bluetooth: <BluetoothIcon fontSize="small" sx={{ color: '#26a69a' }} />,
  cellular: <CellIcon fontSize="small" sx={{ color: '#7e57c2' }} />,
};

interface SignalRow extends SignalEvent {
  _id: number;
  _ts: Date;
}

let _idCounter = 0;

export interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

interface SignalWaterfallTabProps {
  selectedDeviceIds?: string[];
  deviceIdToEntity?: Record<string, string>;
  /** When set, only list signals whose lat/lon fall inside the current map view. */
  mapBounds?: MapBounds | null;
  /** Fly the COP map to a signal observation. */
  onZoomToLocation?: (lat: number, lng: number) => void;
}

const signalInBounds = (s: SignalRow, bounds: MapBounds | null | undefined): boolean => {
  if (!bounds) return true;
  const lat = Number(s.latitude);
  const lon = Number(s.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false;
  if (lat < bounds.south || lat > bounds.north) return false;
  // Handle antimeridian-crossing views (west > east).
  if (bounds.west <= bounds.east) {
    return lon >= bounds.west && lon <= bounds.east;
  }
  return lon >= bounds.west || lon <= bounds.east;
};

const signalCoords = (s: SignalRow): { lat: number; lon: number } | null => {
  const lat = Number(s.latitude);
  const lon = Number(s.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (lat === 0 && lon === 0) return null;
  return { lat, lon };
};

const SignalWaterfallTab: React.FC<SignalWaterfallTabProps> = ({
  selectedDeviceIds,
  deviceIdToEntity,
  mapBounds,
  onZoomToLocation,
}) => {
  const [signals, setSignals] = useState<SignalRow[]>([]);
  const [paused, setPaused] = useState(false);
  const [showWifi, setShowWifi] = useState(true);
  const [showBle, setShowBle] = useState(true);
  const [showCell, setShowCell] = useState(true);
  // Off by default — a broken/empty map extent would otherwise hide every row.
  const [filterByMapExtent, setFilterByMapExtent] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [connected, setConnected] = useState(false);
  const [watchlistDraft, setWatchlistDraft] = useState<WatchlistBundle | null>(null);
  const [watchlistSaving, setWatchlistSaving] = useState(false);
  const [watchlistError, setWatchlistError] = useState<string | null>(null);
  const [watchlistSuccess, setWatchlistSuccess] = useState<string | null>(null);
  const intelEnabled = useFeatureFlagsStore((s) => s.intel);
  const [promoteTarget, setPromoteTarget] = useState<PromoteTarget | null>(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrError, setQrError] = useState<string | null>(null);
  const pausedRef = useRef(paused);
  const bufferedRef = useRef<SignalRow[]>([]);

  useEffect(() => { pausedRef.current = paused; }, [paused]);

  useEffect(() => {
    sseSignalService.connect();
    const checkConn = setInterval(() => setConnected(sseSignalService.isConnected()), 2000);
    let sseReceived = false;

    const unsub = sseSignalService.subscribe((evt: SignalEvent) => {
      sseReceived = true;
      const row: SignalRow = { ...evt, _id: ++_idCounter, _ts: new Date() };
      if (pausedRef.current) {
        bufferedRef.current.push(row);
        if (bufferedRef.current.length > MAX_SIGNALS) bufferedRef.current.shift();
      } else {
        setSignals(prev => [row, ...prev].slice(0, MAX_SIGNALS));
      }
    });

    // DB poll fallback: if SSE delivers nothing after 5s, fetch recent signals from PostgreSQL
    const fallbackTimer = setTimeout(async () => {
      if (sseReceived) return;
      try {
        const apiBase = process.env.REACT_APP_API_URL || '/api';
        const resp = await fetch(`${apiBase}/sse/signals/recent?minutes=10&limit=200`);
        if (resp.ok) {
          const data = await resp.json();
          if (data.length > 0) {
            const rows: SignalRow[] = data.map((d: any) => ({
              ...d,
              _id: ++_idCounter,
              _ts: new Date(d.timestamp || Date.now()),
            }));
            setSignals(rows.slice(0, MAX_SIGNALS));
          }
        }
      } catch { /* ignore fallback errors */ }
    }, 5000);

    // Continue polling DB every 15s if SSE never connects
    const pollInterval = setInterval(async () => {
      if (sseReceived) return;
      try {
        const apiBase = process.env.REACT_APP_API_URL || '/api';
        const resp = await fetch(`${apiBase}/sse/signals/recent?minutes=5&limit=100`);
        if (resp.ok) {
          const data = await resp.json();
          if (data.length > 0) {
            const rows: SignalRow[] = data.map((d: any) => ({
              ...d,
              _id: ++_idCounter,
              _ts: new Date(d.timestamp || Date.now()),
            }));
            setSignals(prev => {
              const merged = [...rows, ...prev];
              const seen = new Set<string>();
              return merged.filter(r => {
                const key = `${r.signal_type}-${r.timestamp}-${(r as any).bssid || (r as any).mac_address || r._id}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
              }).slice(0, MAX_SIGNALS);
            });
          }
        }
      } catch { /* ignore */ }
    }, 15000);

    return () => {
      unsub();
      clearInterval(checkConn);
      clearTimeout(fallbackTimer);
      clearInterval(pollInterval);
      sseSignalService.disconnect();
    };
  }, []);

  const handleResume = useCallback(() => {
    setPaused(false);
    if (bufferedRef.current.length > 0) {
      setSignals(prev => [...bufferedRef.current.reverse(), ...prev].slice(0, MAX_SIGNALS));
      bufferedRef.current = [];
    }
  }, []);

  const handleZoom = useCallback((s: SignalRow, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const coords = signalCoords(s);
    if (!coords || !onZoomToLocation) return;
    onZoomToLocation(coords.lat, coords.lon);
  }, [onZoomToLocation]);

  const openWatchlist = useCallback((s: SignalRow, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setWatchlistError(null);
    setWatchlistSuccess(null);
    const draft = bundleWatchlistDraft(s, signals);
    if (!draft) {
      setWatchlistError('This signal has no identifier to watchlist.');
      return;
    }
    setWatchlistDraft(draft);
  }, [signals]);

  const submitWatchlist = useCallback(async () => {
    if (!watchlistDraft) return;
    const chosen = selectedWatchlistIdentities(watchlistDraft);
    if (!chosen.length) return;
    setWatchlistSaving(true);
    setWatchlistError(null);
    const failed: string[] = [];
    let added = 0;
    try {
      for (const row of chosen) {
        try {
          await apiService.client.post('/admin/dfp/watchlist', {
            identifier: row.identifier.trim(),
            identifier_type: row.identifier_type,
            match_mode: 'exact',
            signal_type: row.signal_type,
            severity_floor: watchlistDraft.severity_floor,
            label: watchlistDraft.label.trim() || null,
            reason: watchlistDraft.reason.trim() || null,
            enabled: true,
          }, { timeout: 30000 });
          added += 1;
        } catch (err: any) {
          failed.push(row.identifier);
        }
      }
      if (added === 0) {
        setWatchlistError(`Failed to add ${failed.join(', ')}`);
        return;
      }
      setWatchlistSuccess(
        failed.length
          ? `Added ${added} identit${added === 1 ? 'y' : 'ies'}; skipped ${failed.join(', ')}`
          : `Added ${added} watchlist identit${added === 1 ? 'y' : 'ies'}`,
      );
      setWatchlistDraft(null);
    } finally {
      setWatchlistSaving(false);
    }
  }, [watchlistDraft]);

  const filtered = useMemo(() => {
    let items = signals;
    if (filterByMapExtent && mapBounds) {
      items = items.filter(s => signalInBounds(s, mapBounds));
    }
    if (!showWifi) items = items.filter(s => s.signal_type !== 'wifi');
    if (!showBle) items = items.filter(s => s.signal_type !== 'bluetooth');
    if (!showCell) items = items.filter(s => s.signal_type !== 'cellular');
    if (selectedDeviceIds && selectedDeviceIds.length > 0) {
      items = items.filter(s => selectedDeviceIds.includes(String(s.device_id)));
    }
    if (search) {
      const q = search.toLowerCase();
      items = items.filter(s =>
        (s.ssid || '').toLowerCase().includes(q) ||
        (s.name || '').toLowerCase().includes(q) ||
        (s.bssid || '').toLowerCase().includes(q) ||
        (s.address || '').toLowerCase().includes(q) ||
        (s.device_type || '').toLowerCase().includes(q) ||
        (s.operator_name || '').toLowerCase().includes(q) ||
        String(s.device_id || '').toLowerCase().includes(q) ||
        (deviceIdToEntity?.[String(s.device_id)] || '').toLowerCase().includes(q)
      );
    }
    return items;
  }, [signals, showWifi, showBle, showCell, search, selectedDeviceIds, mapBounds, filterByMapExtent, deviceIdToEntity]);

  const getLabel = (s: SignalRow) => {
    if (s.signal_type === 'wifi') return s.ssid || s.bssid || '—';
    if (s.signal_type === 'bluetooth') return s.name || s.address || '—';
    return s.operator_name || s.cell_tower_id || '—';
  };

  const openPromote = (s: SignalRow, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const draft = bundleWatchlistDraft(s, signals);
    const primary = draft ? selectedWatchlistIdentities(draft)[0] || draft.identities[0] : null;
    const label = draft?.label || getLabel(s);
    if (primary) {
      setPromoteTarget({
        member_type: 'watchlist',
        member_id: primary.identifier,
        label,
        is_primary: true,
        meta: {
          identifier_type: primary.identifier_type,
          signal_type: primary.signal_type,
          collector_device_id: s.device_id,
        },
      });
      return;
    }
    if (s.device_id) {
      setPromoteTarget({
        member_type: 'device',
        member_id: String(s.device_id),
        label: deviceIdToEntity?.[String(s.device_id)] || String(s.device_id),
        is_primary: true,
      });
    }
  };

  const getRssi = (s: SignalRow): number | null => {
    const v = s.rssi;
    if (v === null || v === undefined) return null;
    return typeof v === 'string' ? parseInt(v, 10) : v;
  };

  const rssiBar = (rssi: number | null) => {
    if (rssi === null || isNaN(rssi)) return null;
    const pct = Math.max(0, Math.min(100, ((rssi + 100) / 60) * 100));
    const color = pct > 66 ? '#4caf50' : pct > 33 ? '#ff9800' : '#f44336';
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ width: 55, height: 6, bgcolor: 'action.hover', borderRadius: 3, overflow: 'hidden', flexShrink: 0 }}>
          <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: color, borderRadius: 3 }} />
        </Box>
        <Typography variant="caption" sx={{ fontSize: '0.7rem', width: 35, color: 'text.secondary', textAlign: 'right' }}>
          {rssi}
        </Typography>
      </Box>
    );
  };

  const formatTime = (d: Date) => d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, flexWrap: 'wrap', borderBottom: 1, borderColor: 'divider' }}>
        <Tooltip title={connected ? 'Connected' : 'Disconnected'}>
          <DotIcon sx={{ fontSize: 12, color: connected ? '#4caf50' : '#f44336' }} />
        </Tooltip>
        <FormControlLabel control={<Switch size="small" checked={showWifi} onChange={(_, v) => setShowWifi(v)} />}
          label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}><WifiIcon sx={{ fontSize: 14 }} /><Typography variant="caption">WiFi</Typography></Box>} sx={{ mr: 0 }} />
        <FormControlLabel control={<Switch size="small" checked={showBle} onChange={(_, v) => setShowBle(v)} />}
          label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}><BluetoothIcon sx={{ fontSize: 14 }} /><Typography variant="caption">BLE</Typography></Box>} sx={{ mr: 0 }} />
        <FormControlLabel control={<Switch size="small" checked={showCell} onChange={(_, v) => setShowCell(v)} />}
          label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}><CellIcon sx={{ fontSize: 14 }} /><Typography variant="caption">Cell</Typography></Box>} sx={{ mr: 0 }} />
        <Tooltip title={mapBounds ? 'Only show signals inside the current map view' : 'Map extent not available yet'}>
          <span>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={filterByMapExtent}
                  disabled={!mapBounds}
                  onChange={(_, v) => setFilterByMapExtent(v)}
                />
              }
              label={<Typography variant="caption">Map</Typography>}
              sx={{ mr: 0 }}
            />
          </span>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <TextField size="small" placeholder="Search SSID / collector..." value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
          sx={{ maxWidth: 180, '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 } }} />
        <Tooltip title={paused ? 'Resume' : 'Pause'}>
          <IconButton size="small" onClick={() => paused ? handleResume() : setPaused(true)}>
            {paused ? <Badge badgeContent={bufferedRef.current.length || undefined} color="error" max={99}><PlayIcon fontSize="small" /></Badge> : <PauseIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Clear">
          <IconButton size="small" onClick={() => { setSignals([]); bufferedRef.current = []; }}>
            <ClearIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="MQTT setup QR (Pathfinder Monitor / Network Survey)">
          <IconButton
            size="small"
            onClick={() => {
              setQrError(null);
              setQrDialogOpen(true);
            }}
          >
            <QrCodeIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
          {filterByMapExtent && mapBounds ? `${filtered.length} in view` : `${filtered.length} signals`}
        </Typography>
      </Box>

      <MqttSetupQrDialog
        open={qrDialogOpen}
        onClose={() => setQrDialogOpen(false)}
        onError={(message) => setQrError(message)}
      />
      <Snackbar
        open={!!qrError}
        autoHideDuration={8000}
        onClose={() => setQrError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={() => setQrError(null)} variant="filled" sx={{ maxWidth: 420 }}>
          {qrError}
        </Alert>
      </Snackbar>

      {(watchlistError || watchlistSuccess) && (
        <Alert
          severity={watchlistError ? 'error' : 'success'}
          onClose={() => { setWatchlistError(null); setWatchlistSuccess(null); }}
          sx={{ mx: 1, mt: 0.5, py: 0, '& .MuiAlert-message': { fontSize: '0.7rem' } }}
        >
          {watchlistError || watchlistSuccess}
        </Alert>
      )}

      {/* Signal Table */}
      <Box sx={{ flex: 1, overflow: 'auto', fontSize: '0.75rem' }}>
        {/* Table header */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
          <Typography variant="caption" sx={{ width: 52, flexShrink: 0, fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.5 }}>Time</Typography>
          <Typography variant="caption" sx={{ width: 16, flexShrink: 0, fontSize: '0.55rem', fontWeight: 700, opacity: 0.5 }}></Typography>
          <Typography variant="caption" sx={{ flex: 1, fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.5, pl: 0.75 }}>Name</Typography>
          <Typography variant="caption" sx={{ width: 42, flexShrink: 0, fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.5 }}>Type</Typography>
          <Typography variant="caption" sx={{ width: 90, flexShrink: 0, fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.5 }}>RSSI</Typography>
          <Typography variant="caption" sx={{ width: 56, flexShrink: 0, fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.5, textAlign: 'right' }}>Act</Typography>
        </Box>

        {filtered.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            <RssiIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
            <Typography variant="body2">
              {signals.length === 0
                ? 'Waiting for signals...'
                : filterByMapExtent && mapBounds
                  ? 'No signals in current map view'
                  : 'No matching signals'}
            </Typography>
          </Box>
        )}
        {filtered.map(s => {
          const isExpanded = expandedId === s._id;
          const rssi = getRssi(s);
          const typeAbbrev = s.signal_type === 'wifi' ? 'WiFi' : s.signal_type === 'bluetooth' ? 'BT' : 'Cell';
          const dotColor = s.signal_type === 'wifi' ? '#42a5f5' : s.signal_type === 'bluetooth' ? '#26a69a' : '#7e57c2';
          const coords = signalCoords(s);
          const canZoom = !!coords && !!onZoomToLocation;
          const canWatch = !!bundleWatchlistDraft(s);
          return (
            <Box key={s._id} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
              {/* Table row */}
              <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, cursor: 'pointer',
                transition: 'background 0.12s',
                '&:hover': { bgcolor: 'action.hover' },
              }} onClick={() => setExpandedId(isExpanded ? null : s._id)}>
                {/* Time */}
                <Typography variant="caption" sx={{ width: 52, flexShrink: 0, fontSize: '0.65rem', color: 'text.secondary', fontFamily: '"Roboto Mono", monospace' }}>
                  {formatTime(s._ts)}
                </Typography>
                {/* Signal icon */}
                <Box sx={{ width: 16, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                  {SIGNAL_ICONS[s.signal_type]}
                </Box>
                {/* Name / Label + collector */}
                <Box sx={{ flex: 1, minWidth: 0, pl: 0.75, pr: 0.5 }}>
                  <Typography variant="caption" noWrap sx={{ display: 'block', fontSize: '0.7rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {getLabel(s)}
                  </Typography>
                  {(deviceIdToEntity?.[String(s.device_id)] || s.device_id) && (
                    <Typography variant="caption" noWrap sx={{ display: 'block', fontSize: '0.58rem', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {deviceIdToEntity?.[String(s.device_id)] || String(s.device_id).slice(0, 10)}
                    </Typography>
                  )}
                </Box>
                {/* Device type abbreviation with dot */}
                <Box sx={{ width: 42, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: dotColor }} />
                  <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>{typeAbbrev}</Typography>
                </Box>
                {/* RSSI bar */}
                <Box sx={{ width: 90, flexShrink: 0 }}>{rssiBar(rssi)}</Box>
                {/* Quick actions */}
                <Box sx={{ width: intelEnabled ? 78 : 56, flexShrink: 0, display: 'flex', justifyContent: 'flex-end', gap: 0 }}>
                  <Tooltip title={canZoom ? 'Zoom to location' : 'No location'}>
                    <span>
                      <IconButton size="small" disabled={!canZoom} onClick={(e) => handleZoom(s, e)} sx={{ p: 0.25 }}>
                        <ZoomIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={canWatch ? 'Add to watchlist' : 'No identifier'}>
                    <span>
                      <IconButton size="small" disabled={!canWatch} onClick={(e) => openWatchlist(s, e)} sx={{ p: 0.25 }}>
                        <WatchlistIcon sx={{ fontSize: 14, color: canWatch ? '#f44336' : undefined }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {intelEnabled && (
                    <Tooltip title="Add to Intel Packet">
                      <span>
                        <IconButton
                          size="small"
                          disabled={!canWatch && !s.device_id}
                          onClick={(e) => openPromote(s, e)}
                          sx={{ p: 0.25 }}
                        >
                          <FactCheckIcon sx={{ fontSize: 14, color: '#ffb300' }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </Box>
              {/* Expanded Detail */}
              <Collapse in={isExpanded}>
                <Box sx={{ px: 1.5, py: 1, borderTop: '1px dashed', borderColor: 'divider', bgcolor: 'action.hover', fontSize: '0.7rem' }}>
                  {/* Actions */}
                  <Box sx={{ display: 'flex', gap: 0.75, mb: 1, flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      icon={<ZoomIcon sx={{ fontSize: '14px !important' }} />}
                      label="Zoom to location"
                      clickable={canZoom}
                      disabled={!canZoom}
                      onClick={(e) => handleZoom(s, e)}
                      sx={{ height: 22, fontSize: '0.65rem' }}
                    />
                    <Chip
                      size="small"
                      icon={<WatchlistIcon sx={{ fontSize: '14px !important', color: '#f44336 !important' }} />}
                      label="Add to watchlist"
                      clickable={canWatch}
                      disabled={!canWatch}
                      onClick={(e) => openWatchlist(s, e)}
                      sx={{ height: 22, fontSize: '0.65rem', color: '#f44336', borderColor: 'rgba(244,67,54,0.4)' }}
                      variant="outlined"
                    />
                    {intelEnabled && (
                      <Chip
                        size="small"
                        icon={<FactCheckIcon sx={{ fontSize: '14px !important', color: '#ffb300 !important' }} />}
                        label="Add to packet"
                        clickable
                        disabled={!canWatch && !s.device_id}
                        onClick={(e) => openPromote(s, e)}
                        sx={{ height: 22, fontSize: '0.65rem', color: '#ffb300', borderColor: 'rgba(255,179,0,0.4)' }}
                        variant="outlined"
                      />
                    )}
                  </Box>
                  {/* Quick info chips */}
                  <Box sx={{ display: 'flex', gap: 0.5, mb: 0.75, flexWrap: 'wrap' }}>
                    <Chip label={s.device_type || 'Unknown'} size="small"
                      sx={{ bgcolor: DEVICE_TYPE_COLORS[s.device_type || 'Unknown'], color: '#fff', fontSize: '0.55rem', height: 18, '& .MuiChip-label': { px: 0.75 } }} />
                    {deviceIdToEntity?.[String(s.device_id)] && (
                      <Chip label={deviceIdToEntity[String(s.device_id)]} size="small" variant="outlined"
                        sx={{ fontSize: '0.55rem', height: 18, borderColor: 'primary.main', color: 'primary.main' }} />
                    )}
                    {s.badges?.map(b => (
                      <Chip key={b} label={b} size="small" variant="outlined" sx={{ fontSize: '0.55rem', height: 18 }} />
                    ))}
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 0.5 }}>
                    {s.signal_type === 'wifi' && (<>
                      <DetailItem label="BSSID" value={s.bssid} />
                      <DetailItem label="SSID" value={s.ssid} />
                      <DetailItem label="Frequency" value={s.frequency ? `${s.frequency} MHz` : null} />
                      <DetailItem label="Security" value={s.security} />
                      <DetailItem label="Standard" value={s.standard} />
                      <DetailItem label="Passpoint" value={s.is_passpoint ? 'Yes' : null} />
                      <DetailItem label="Operator" value={s.operator_friendly_name} />
                      <DetailItem label="Venue" value={s.venue_name} />
                      <DetailItem label="RTT" value={s.is_80211mc_responder ? '802.11mc' : null} />
                      {s.information_elements && (
                        <Box sx={{ mt: 0.5, gridColumn: '1 / -1' }}>
                          <WiFiIEPanel informationElements={s.information_elements} isDarkMode={true} />
                        </Box>
                      )}
                    </>)}
                    {s.signal_type === 'bluetooth' && (<>
                      <DetailItem label="Address" value={s.address} />
                      <DetailItem label="Name" value={s.name} />
                      <DetailItem label="Type" value={s.bt_type} />
                      <DetailItem label="Mfr ID" value={s.manufacturer_id != null ? `0x${s.manufacturer_id.toString(16).toUpperCase().padStart(4, '0')}` : null} />
                      <DetailItem label="Mfr Data" value={s.manufacturer_data} mono />
                      <DetailItem label="UUIDs" value={s.service_uuids} mono />
                      <DetailItem label="TX Power" value={s.tx_power_level != null ? `${s.tx_power_level} dBm` : null} />
                      <DetailItem label="Connectable" value={s.is_connectable != null ? (s.is_connectable ? 'Yes' : 'No') : null} />
                      <DetailItem label="Randomized" value={s.is_randomized ? 'Yes' : null} />
                      <DetailItem label="Distance" value={s.estimated_distance_m ? `~${s.estimated_distance_m}m` : null} />
                    </>)}
                    {s.signal_type === 'cellular' && (<>
                      <DetailItem label="Cell Tower" value={s.cell_tower_id} />
                      <DetailItem label="Operator" value={s.operator_name || s.operator} />
                      <DetailItem label="Network" value={s.network_type} />
                      <DetailItem label="MCC/MNC" value={s.mcc && s.mnc ? `${s.mcc}/${s.mnc}` : null} />
                    </>)}
                    <DetailItem label="Collector" value={deviceIdToEntity?.[String(s.device_id)] ? `${deviceIdToEntity[String(s.device_id)]} (${s.device_id})` : String(s.device_id)} />
                    <DetailItem label="Position" value={s.latitude && s.longitude ? `${s.latitude}, ${s.longitude}` : null} />
                  </Box>
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>

      {(watchlistError || watchlistSuccess) && (
        <Alert
          severity={watchlistError ? 'error' : 'success'}
          onClose={() => { setWatchlistError(null); setWatchlistSuccess(null); }}
          sx={{ borderRadius: 0, py: 0, '& .MuiAlert-message': { fontSize: '0.75rem' } }}
        >
          {watchlistError || watchlistSuccess}
        </Alert>
      )}

      <Dialog open={!!watchlistDraft} onClose={() => !watchlistSaving && setWatchlistDraft(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WatchlistIcon sx={{ color: '#f44336' }} />
          Add Signal to Threat Watchlist
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              A hit on any selected identifier raises a watchlist alert. Stable IDs are
              checked; rotating hotspot or BLE addresses are left off.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {watchlistDraft?.identities.map((row, index) => (
                <FormControlLabel
                  key={`${row.signal_type}:${row.identifier_type}:${row.identifier}`}
                  sx={{ alignItems: 'flex-start', mr: 0, ml: 0 }}
                  control={(
                    <Checkbox
                      size="small"
                      checked={row.selected}
                      onChange={(_, checked) => setWatchlistDraft((prev) => prev ? ({
                        ...prev,
                        identities: prev.identities.map((item, i) => (
                          i === index ? { ...item, selected: checked } : item
                        )),
                      }) : prev)}
                      sx={{ pt: 0.25 }}
                    />
                  )}
                  label={(
                    <Box>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {row.identifier}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.identifier_type} · {row.signal_type} — {row.note}
                      </Typography>
                    </Box>
                  )}
                />
              ))}
            </Box>
            <FormControl size="small" sx={{ maxWidth: 180 }}>
              <InputLabel>Severity</InputLabel>
              <Select
                label="Severity"
                value={watchlistDraft?.severity_floor || 'critical'}
                onChange={e => setWatchlistDraft(prev => prev ? ({ ...prev, severity_floor: e.target.value as WatchlistBundle['severity_floor'] }) : prev)}
              >
                {(['info', 'warning', 'critical'] as const).map(t => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Label"
              value={watchlistDraft?.label || ''}
              onChange={e => setWatchlistDraft(prev => prev ? ({ ...prev, label: e.target.value }) : prev)}
              fullWidth
            />
            <TextField
              size="small"
              label="Reason"
              placeholder="Why this emitter is a threat"
              value={watchlistDraft?.reason || ''}
              onChange={e => setWatchlistDraft(prev => prev ? ({ ...prev, reason: e.target.value }) : prev)}
              fullWidth
              multiline
              minRows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWatchlistDraft(null)} disabled={watchlistSaving}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={submitWatchlist}
            disabled={watchlistSaving || !watchlistDraft || selectedWatchlistIdentities(watchlistDraft).length === 0}
            startIcon={watchlistSaving ? <CircularProgress size={16} /> : <WatchlistIcon />}
          >
            Add to Watchlist
          </Button>
        </DialogActions>
      </Dialog>
      <PromoteToPacketDialog
        open={!!promoteTarget}
        onClose={() => setPromoteTarget(null)}
        target={promoteTarget}
        defaultName={promoteTarget?.label}
      />
    </Box>
  );
};

const DetailItem: React.FC<{ label: string; value: any; mono?: boolean }> = ({ label, value, mono }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>{label}</Typography>
      <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>
        {String(value)}
      </Typography>
    </Box>
  );
};

export default SignalWaterfallTab;
